import type { WeekDay, PickupScheduleSlot, OrderingQuota } from '@/services/innovorder/restaurant/restaurant.type';

export type TimeSlot = {
    start: string;
    end: string;
    orderingQuotas: OrderingQuota[];
};

export type DaySchedule = {
    day: WeekDay;
    isOpen: boolean;
    slots: TimeSlot[];
};

const ALL_DAYS: WeekDay[] = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] as WeekDay[];

export const minutesToTime = (minutes: number): string => {
    if (minutes >= 1440) return '00:00';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

export const timeToMinutes = (time: string): number => {
    const [h, m] = time.split(':').map(Number);
    if (time === '00:00' && h === 0 && m === 0) return 0;
    return h * 60 + m;
};

export const parseSchedulesToDaySchedules = (slots: PickupScheduleSlot[]): DaySchedule[] => {
    return ALL_DAYS.map((day) => {
        const daySlots = slots.filter((s) => s.day === day);
        return {
            day,
            isOpen: daySlots.length > 0,
            slots: daySlots.map((s) => ({
                start: minutesToTime(s.start),
                end: minutesToTime(s.end),
                orderingQuotas: s.orderingQuotas ?? [],
            })),
        };
    });
};

export const daySchedulesToSlots = (daySchedules: DaySchedule[]): PickupScheduleSlot[] => {
    return daySchedules
        .filter((ds) => ds.isOpen)
        .flatMap((ds) =>
            ds.slots.map((slot) => {
                const start = timeToMinutes(slot.start);
                let end = timeToMinutes(slot.end);
                if (slot.end === '00:00') end = 1440;
                return {
                    day: ds.day,
                    start,
                    end,
                    orderingQuotas: slot.orderingQuotas.length > 0 ? slot.orderingQuotas : undefined,
                };
            }),
        );
};

export const getDefaultSlot = (): TimeSlot => ({
    start: '09:00',
    end: '13:00',
    orderingQuotas: [],
});

export const generateTimeOptions = (): string[] => {
    const options: string[] = [];
    Array.from({ length: 24 }, (_, h) => h).forEach((h) => {
        Array.from({ length: 4 }, (_, i) => i * 15).forEach((m) => {
            options.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
        });
    });
    return options;
};

export const generateMassScheduleSlots = (
    days: WeekDay[],
    startTime: string,
    endTime: string,
    granularity: number,
): { day: WeekDay; start: number; end: number }[] => {
    const startMin = timeToMinutes(startTime);
    let endMin = timeToMinutes(endTime);
    if (endTime === '00:00') endMin = 1440;

    return days.flatMap((day) => {
        const daySlots: { day: WeekDay; start: number; end: number }[] = [];
        let current = startMin;
        while (current + granularity <= endMin) {
            daySlots.push({ day, start: current, end: current + granularity });
            current += granularity;
        }
        return daySlots;
    });
};

export const DAY_LABELS: Record<WeekDay, string> = {
    MON: 'restaurantInfo.schedule.day.mon',
    TUE: 'restaurantInfo.schedule.day.tue',
    WED: 'restaurantInfo.schedule.day.wed',
    THU: 'restaurantInfo.schedule.day.thu',
    FRI: 'restaurantInfo.schedule.day.fri',
    SAT: 'restaurantInfo.schedule.day.sat',
    SUN: 'restaurantInfo.schedule.day.sun',
} as Record<WeekDay, string>;
